import { every, last, get, head, size } from 'lodash';
import { createSelector } from 'reselect';
import createSelectors from '../common/utils/selectorUtil';
import * as fromBook from './bookSelectors';
import { VR_RESULT_STATUS, VR_ERROR_REASONS } from '../constants/error';
import { TRO_PLAYLIST } from '../common/components/demoVideos/demoVideoConstants';

const { READY, PENDING, FAILED, UNAVAILABLE} = VR_RESULT_STATUS;
const { TIMEOUT, UNTRANSCRIBABLE, UNKNOWN } = VR_ERROR_REASONS;

export const selectors = {
    ...createSelectors(fromBook, 'book'),
};

export const getIsTRCDataFetched = state => state.isDataFetched;

export const getTRCActiveDemoVideoId = state => state.activeVideoId;

export const getTRCDemoVideosProgress = state => state.demoVideosProgress;

export const getTRCBookResults = state => state.bookResults;

export const selectVerificationStatus = state => state.verificationStatus;

export const selectVerificationResult = state => state.verificationResult;

export const getOnboardingProgressBar = state => state.onboardingProgressBar;

export const areAllDemoVideosWatched = state =>
    size(state.demoVideosProgress) === size(TRO_PLAYLIST)
    && every(state.demoVideosProgress, { watched: true });

export const getBookIndex = createSelector(
    getTRCBookResults,
    (results) => results.length - 1,
);

export const getIsFirstTRCBook = createSelector(
    getBookIndex,
    (index) => index === 0,
);

export const getTRCSelectedResponses = state => state.selectedResponses;

export const getTRCSelectedResponse = (state, questionIndex) => getTRCSelectedResponses(state)[questionIndex];

export const getTRCComprehensionResult = (state, questionIndex, duration) => {
    const comprehensionItem = selectors.getTRCComprehensionItem(state, questionIndex);
    const result = {
        item_idx: questionIndex,
        book_id: selectors.getTRCBookId(state),
        item_type: comprehensionItem.type,
        time_duration: duration,
        response: getTRCSelectedResponse(state, questionIndex),
    };

    return result;
};

export const getTRCBookPassingLevel = createSelector(
    [getTRCBookResults, getBookIndex],
    (results, bookIndex) => results[bookIndex],
);

export const getTRCBookPrevPassingLevel = createSelector(
    [getTRCBookResults, getBookIndex],
    (results, bookIndex) => results[bookIndex - 1],
);

export const getTRCReadingLevel = createSelector(
    getTRCBookResults,
    (results) => {
        if (results.length) {
            return results[results.length - 1].readingLevel;
        }
        return undefined;
    },
);

export const getCurrentBookResult = result => last(get(result, 'measure_result.book_results', []));

export const getVRResultsStatus = createSelector(getTRCBookResults, results =>
    results.map(result => result.vrStatus));

export const getVRErrorReasons = createSelector(getTRCBookResults, results =>
    results.map(result => result.vrErrorReason));


export const getVrScores = createSelector(getTRCBookResults, results => results.map(result => result.vrScore));

export const getIsComprehensionComplete = createSelector(getTRCBookResults, results =>
    results.length > 0 && results.every(result => result.isComprehensionComplete),
);

export const getIsVrResultUnavailable = createSelector(getVRResultsStatus, statuses => statuses.includes(UNAVAILABLE));

export const getIsVrResultInPendingState = createSelector(getVRResultsStatus, statuses => statuses.includes(PENDING));

export const getIsVrResultInFailedState = createSelector(getVRResultsStatus, statuses => statuses.includes(FAILED));

export const getIsVrResultErrorTimedOut = createSelector(getVRErrorReasons, reasons => reasons.includes(TIMEOUT));

export const getIsVrResultErrorUntranscribable = createSelector(getVRErrorReasons, reasons => reasons.includes(UNTRANSCRIBABLE));

export const getIsVrResultErrorUnknown = createSelector(getVRErrorReasons, reasons => reasons.includes(UNKNOWN));

export const getIsVrResultProcessing = createSelector(getIsVrResultInPendingState, getIsVrResultErrorTimedOut,
    (isPending, isTimedOut) =>
        isPending || isTimedOut
);

export const getHasUnscoredUntranscribableResult = createSelector(getVRErrorReasons, getVrScores, (reasons, scores) =>
    reasons.some((reason, index) => reason === UNTRANSCRIBABLE && scores[index] === null)
);

export const getHasUnscoredUnknownErrorResult = createSelector(getVRErrorReasons, getVrScores, (reasons, scores) =>
    reasons.some((reason, index) => reason === UNKNOWN && scores[index] === null)
);

export const getHasUnscoredFailedResult = createSelector(getVRResultsStatus, getVrScores, (statuses, scores) =>
    statuses.some((status, index) => status === FAILED && scores[index] === null)
);

export const getAllVrResultsHaveScores = createSelector(getVrScores, vrScores => vrScores.every(vrScore => vrScore !== null));

export const getIsOnlyFirstBookVrReady = createSelector(getTRCBookResults, bookResults =>
    size(bookResults) === 1 && get(head(bookResults), 'vrStatus') === READY);

export const getAreAllBooksVrReady = createSelector(getTRCBookResults, bookResults =>
    size(bookResults) === 2 && bookResults.every(({ vrStatus }) => vrStatus === READY));

export const getLatestBookResult = createSelector(getTRCBookResults, results =>
    results[results.length - 1]
);

export const getIsReadingComplete = state => state.isReadingComplete;

export const getDidApiCallSucceed = state => state.didApiCallSucceed;

export const getDidApiCallFail = state => state.didApiCallFail;

export const getPrevApiRequest = state => state.prevApiRequest;

export const getDidAttemptMicReadingCheck = state => state.readingCheck.didAttemptMicReadingCheck;

export const getDidAttemptMultiChoiceReadingCheck = state => state.readingCheck.didAttemptMultiChoiceReadingCheck;

export const getPassedReadingCheck = state => state.readingCheck.passedMicRead || state.readingCheck.passedMultipleChoiceRead;

export const getPassedMicReadingCheck = state => state.readingCheck.passedMicRead;

export const getTRCComprehensionAttempts = state => state.comprehensionAttempts;

export const getDidPreviouslyEnableMic = state => state.didPreviouslyEnableMic;
